import React, { useState, useEffect, useCallback } from "react";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import { themeAtom } from "../../../../../-Atoms";
import { db, doc, updateDoc } from "../../../../../firebaseConfig";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";
import debounce from "lodash/debounce";

const roundUpToNearest5 = (date) => {
  const minutes = date.minute();
  const remainder = 5 - (minutes % 5);
  return date.add(remainder, "minute").second(0);
};

const formatOvertime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours > 0) {
    return `${hours} Hour${hours > 1 ? "s" : ""} ${
      remainingMinutes > 0
        ? `${remainingMinutes} Minute${remainingMinutes > 1 ? "s" : ""}`
        : ""
    }`;
  }
  return `${remainingMinutes} Minute${remainingMinutes > 1 ? "s" : ""}`;
};

const stripDate = (date) => {
  return dayjs().hour(date.hour()).minute(date.minute()).second(0);
};

const DatesV2 = ({
  bookedStart,
  bookedFinish,
  startTime,
  setStartTime,
  jobData,
  finishTime,
  setFinishTime,
  overtime,
  setOvertime,
}) => {
  const [theme] = useAtom(themeAtom);
  const [isStartDisabled, setIsStartDisabled] = useState(false);
  const [isFinishDisabled, setIsFinishDisabled] = useState(false);
  const [manualOvertime, setManualOvertime] = useState(false);
  
  // Date states
  const [dueDate, setDueDate] = useState(
    jobData.dueDate ? dayjs(jobData.dueDate) : null
  );
  const [expressDueDate, setExpressDueDate] = useState(
    jobData.expressDueDate ? dayjs(jobData.expressDueDate) : null
  );

  const updateField = (id, field, value) => {
    updateDoc(doc(db, "JobData", id), { [field]: value })
      .catch((error) => {
        console.log(error);
      });
  };

  // Debounced update for overtime field
  const debouncedUpdateOvertime = useCallback(
    debounce((value) => {
      updateField(jobData.id, "overtime", value);
    }, 500),
    [jobData.id]
  );

  // Sync date states with jobData changes
  useEffect(() => {
    setDueDate(jobData.dueDate ? dayjs(jobData.dueDate) : null);
    setExpressDueDate(
      jobData.expressDueDate ? dayjs(jobData.expressDueDate) : null
    );
  }, [jobData]);

  useEffect(() => {
    if (startTime && finishTime && !manualOvertime) {
      const actualStart = stripDate(dayjs(startTime));
      const actualFinish = stripDate(dayjs(finishTime));
      const bookedStartTime = stripDate(dayjs(bookedStart));
      const bookedFinishTime = stripDate(dayjs(bookedFinish));

      const actualDuration = actualFinish.diff(actualStart, "minute");
      const bookedDuration = bookedFinishTime.diff(bookedStartTime, "minute");

      const overtimeMinutes = actualDuration - bookedDuration;
      if (overtimeMinutes > 0) {
        const formattedOvertime = formatOvertime(overtimeMinutes);
        setOvertime(formattedOvertime);
        updateField(jobData.id, "overtime", formattedOvertime);
      } else {
        setOvertime("");
        updateField(jobData.id, "overtime", "");
      }
    }
  }, [
    startTime,
    finishTime,
    bookedStart,
    bookedFinish,
    jobData.id,
    setOvertime,
    manualOvertime,
  ]);

  const handleStartTimeChange = (d) => {
    if (!d || !d.isValid()) return;
    const isoString = d.toISOString();
    setStartTime(isoString);
    updateField(jobData.id, "startTime", isoString);
    setIsFinishDisabled(false);
    setManualOvertime(false); // Reset manual input if time changes
  };

  const handleFinishTimeChange = (d) => {
    if (!d || !d.isValid()) return;
    const isoString = d.toISOString();
    setFinishTime(isoString);
    updateField(jobData.id, "finishTime", isoString);
    setIsStartDisabled(false);
    setManualOvertime(false); // Reset manual input if time changes
  };

  const handleOvertimeChange = (value) => {
    setOvertime(value);
    setManualOvertime(true); // Set flag for manual overtime input
    debouncedUpdateOvertime(value); // Debounced update
  };

  // Date handlers
  const handleDueDateChange = (newValue) => {
    const dateValue = newValue ? dayjs(newValue) : null;
    setDueDate(dateValue);
    updateField(jobData.id, "dueDate", dateValue ? dateValue.toISOString() : null);
  };

  const handleExpressDueDateChange = (newValue) => {
    const dateValue = newValue ? dayjs(newValue) : null;
    setExpressDueDate(dateValue);
    updateField(jobData.id, "expressDueDate", dateValue ? dateValue.toISOString() : null);
  };


  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
  };

  return (
    <motion.div variants={itemVariants}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "10px",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 2,
          marginBottom: 2,
        }}
      >
        
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-au">
            {/* Date Fields Row */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "nowrap" }}>
              <MobileDatePicker
                label="Shoot Date"
                format="ddd D/M/YYYY"
                value={jobData.bookedStart ? dayjs(jobData.bookedStart) : null}
                disabled
                sx={{ flex: "1 1 48%", minWidth: "200px" }}
                slotProps={{
                  textField: {
                    variant: "filled",
                    fullWidth: true,
                    size: "small",
                    inputProps: {
                      style: { fontSize: 15 },
                    },
                    InputLabelProps: {
                      style: { fontSize: 15 },
                    },
                    InputProps: {
                      disableUnderline: true,
                    },
                    sx: {
                      "& .MuiFilledInput-root": {
                        borderRadius: "8px",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(45,45,45,0.7)"
                            : "rgba(250,250,250,0.9)",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(55,55,55,0.8)"
                              : "rgba(255,255,255,1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(60,60,60,0.9)"
                              : "rgba(255,255,255,1)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0 0 2px rgba(100,100,255,0.5)"
                              : "0 0 0 2px rgba(100,100,255,0.3)",
                        },
                      },
                    },
                  },
                }}
              />
              <MobileDatePicker
                label="Due Date"
                format="ddd D/M/YYYY"
                value={dueDate}
                onChange={handleDueDateChange}
                disabled
                sx={{ flex: "1 1 48%", minWidth: "200px" }}
                slotProps={{
                  textField: {
                    variant: "filled",
                    fullWidth: true,
                    size: "small",
                    inputProps: {
                      style: { fontSize: 15 },
                    },
                    InputLabelProps: {
                      style: { fontSize: 15 },
                    },
                    InputProps: {
                      disableUnderline: true,
                    },
                    sx: {
                      "& .MuiFilledInput-root": {
                        borderRadius: "8px",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(45,45,45,0.7)"
                            : "rgba(250,250,250,0.9)",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(55,55,55,0.8)"
                              : "rgba(255,255,255,1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(60,60,60,0.9)"
                              : "rgba(255,255,255,1)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0 0 2px rgba(100,100,255,0.5)"
                              : "0 0 0 2px rgba(100,100,255,0.3)",
                        },
                      },
                    },
                  },
                }}
              />
            </Box>

            {/* Express Due Date Row - Conditional */}
            {expressDueDate && (
              <Box sx={{ display: "flex", gap: 2, flexWrap: "nowrap" }}>
                <MobileDatePicker
                  label="Express Due Date"
                  format="ddd D/M/YYYY"
                  value={expressDueDate}
                  onChange={handleExpressDueDateChange}
                  disabled
                  sx={{ flex: "1 1 100%", minWidth: "200px" }}
                  slotProps={{
                    textField: {
                      variant: "filled",
                      fullWidth: true,
                      size: "small",
                      inputProps: {
                        style: { fontSize: 15 },
                      },
                      InputLabelProps: {
                        style: { fontSize: 15 },
                      },
                      InputProps: {
                        disableUnderline: true,
                      },
                      sx: {
                        "& .MuiFilledInput-root": {
                          borderRadius: "8px",
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(45,45,45,0.7)"
                              : "rgba(250,250,250,0.9)",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            backgroundColor:
                              theme === "dark"
                                ? "rgba(55,55,55,0.8)"
                                : "rgba(255,255,255,1)",
                          },
                          "&.Mui-focused": {
                            backgroundColor:
                              theme === "dark"
                                ? "rgba(60,60,60,0.9)"
                                : "rgba(255,255,255,1)",
                            boxShadow:
                              theme === "dark"
                                ? "0 0 0 2px rgba(100,100,255,0.5)"
                                : "0 0 0 2px rgba(100,100,255,0.3)",
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>
            )}

            {/* Booked Times Row */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "nowrap" }}>
              <TimePicker
                label="Booked Start"
                value={bookedStart ? dayjs(bookedStart) : null}
                onChange={() => {}}
                readOnly
                disabled
                sx={{ flex: "1 1 48%", minWidth: "200px" }}
                slotProps={{
                  textField: {
                    variant: "filled",
                    fullWidth: true,
                    size: "small",
                    inputProps: {
                      style: { fontSize: 15 },
                    },
                    InputLabelProps: {
                      style: { fontSize: 15 },
                    },
                    InputProps: {
                      disableUnderline: true,
                    },
                    sx: {
                      "& .MuiFilledInput-root": {
                        borderRadius: "8px",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(45,45,45,0.7)"
                            : "rgba(250,250,250,0.9)",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(55,55,55,0.8)"
                              : "rgba(255,255,255,1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(60,60,60,0.9)"
                              : "rgba(255,255,255,1)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0 0 2px rgba(100,100,255,0.5)"
                              : "0 0 0 2px rgba(100,100,255,0.3)",
                        },
                      },
                    },
                  },
                }}
              />
              <TimePicker
                label="Booked Finish"
                value={bookedFinish ? dayjs(bookedFinish) : null}
                onChange={() => {}}
                readOnly
                disabled
                sx={{ flex: "1 1 48%", minWidth: "200px" }}
                slotProps={{
                  textField: {
                    variant: "filled",
                    fullWidth: true,
                    size: "small",
                    inputProps: {
                      style: { fontSize: 15 },
                    },
                    InputLabelProps: {
                      style: { fontSize: 15 },
                    },
                    InputProps: {
                      disableUnderline: true,
                    },
                    sx: {
                      "& .MuiFilledInput-root": {
                        borderRadius: "8px",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(45,45,45,0.7)"
                            : "rgba(250,250,250,0.9)",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(55,55,55,0.8)"
                              : "rgba(255,255,255,1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(60,60,60,0.9)"
                              : "rgba(255,255,255,1)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0 0 2px rgba(100,100,255,0.5)"
                              : "0 0 0 2px rgba(100,100,255,0.3)",
                        },
                      },
                    },
                  },
                }}
              />
            </Box>
            
            {/* Actual Times Row */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "nowrap" }}>
              <TimePicker
                label="Start Time"
                value={startTime ? dayjs(startTime) : null}
                onChange={handleStartTimeChange}
                onOpen={() => {
                  if (!startTime) {
                    setStartTime(bookedStart);
                    updateField(jobData.id, "startTime", bookedStart);
                  }
                  setIsFinishDisabled(true);
                }}
                onClose={() => setIsFinishDisabled(false)}
                disabled={isStartDisabled}
                sx={{ flex: "1 1 48%", minWidth: "200px" }}
                slotProps={{
                  textField: {
                    variant: "filled",
                    fullWidth: true,
                    size: "small",
                    inputProps: {
                      style: { fontSize: 15 },
                      onInput: (e) => e.preventDefault(),
                    },
                    InputLabelProps: {
                      style: { fontSize: 15 },
                    },
                    InputProps: {
                      disableUnderline: true,
                    },
                    sx: {
                      "& .MuiFilledInput-root": {
                        borderRadius: "8px",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(45,45,45,0.7)"
                            : "rgba(250,250,250,0.9)",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(55,55,55,0.8)"
                              : "rgba(255,255,255,1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(60,60,60,0.9)"
                              : "rgba(255,255,255,1)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0 0 2px rgba(100,100,255,0.5)"
                              : "0 0 0 2px rgba(100,100,255,0.3)",
                        },
                      },
                    },
                  },
                }}
              />
              <TimePicker
                label="Finish Time"
                value={finishTime ? dayjs(finishTime) : null}
                onChange={handleFinishTimeChange}
                onOpen={() => {
                  if (!finishTime) {
                    const now = roundUpToNearest5(dayjs());
                    setFinishTime(now);
                    updateField(jobData.id, "finishTime", now.toISOString());
                  }
                  setIsStartDisabled(true);
                }}
                onClose={() => setIsStartDisabled(false)}
                disabled={isFinishDisabled}
                sx={{ flex: "1 1 48%", minWidth: "200px" }}
                slotProps={{
                  textField: {
                    variant: "filled",
                    fullWidth: true,
                    size: "small",
                    inputProps: {
                      style: { fontSize: 15 },
                      onInput: (e) => e.preventDefault(),
                    },
                    InputLabelProps: {
                      style: { fontSize: 15 },
                    },
                    InputProps: {
                      disableUnderline: true,
                    },
                    sx: {
                      "& .MuiFilledInput-root": {
                        borderRadius: "8px",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(45,45,45,0.7)"
                            : "rgba(250,250,250,0.9)",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(55,55,55,0.8)"
                              : "rgba(255,255,255,1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(60,60,60,0.9)"
                              : "rgba(255,255,255,1)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0 0 2px rgba(100,100,255,0.5)"
                              : "0 0 0 2px rgba(100,100,255,0.3)",
                        },
                      },
                    },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
          
          {/* Overtime Field */}
          <TextField
            variant="filled"
            fullWidth
            size="small"
            multiline
            value={overtime || ""}
            onChange={(e) => handleOvertimeChange(e.target.value)}
            label="Overtime"
            inputProps={{ style: { fontSize: 15 } }}
            InputLabelProps={{ style: { fontSize: 15 } }}
            InputProps={{ disableUnderline: true }}
            sx={{ 
              "& .MuiFilledInput-root": {
                borderRadius: "8px",
                backgroundColor:
                  theme === "dark"
                    ? "rgba(45,45,45,0.7)"
                    : "rgba(250,250,250,0.9)",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(55,55,55,0.8)"
                      : "rgba(255,255,255,1)",
                },
                "&.Mui-focused": {
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(60,60,60,0.9)"
                      : "rgba(255,255,255,1)",
                  boxShadow:
                    theme === "dark"
                      ? "0 0 0 2px rgba(100,100,255,0.5)"
                      : "0 0 0 2px rgba(100,100,255,0.3)",
                },
              },
            }}
          />
        </Box>
      </Box>
    </motion.div>
  );
};

export default DatesV2;